import {
  Tab,
  Tabs,
  FormControl,
  Select,
  MenuItem,
  Typography,
  useTheme,
  useMediaQuery,
  SelectChangeEvent,
  InputLabel,
  Box,
} from '@mui/material';
import React, { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

interface TabItem {
  label: string;
  href: string;
}

const TabsWithOutlet: React.FC<{ tabs: TabItem[] }> = ({ tabs }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Find the current tab index based on the current path
  const currentTabIndex = tabs.findIndex((tab) => location.pathname.startsWith(tab.href));
  const tabValue = currentTabIndex !== -1 ? currentTabIndex : 0;

  // Navigate to the first tab on mount if we're at the parent route
  useEffect(() => {
    // Check if we're at the parent route (e.g., /manage) without a specific tab
    const isAtParentRoute = tabs.every((tab) => !location.pathname.startsWith(tab.href));

    if (isAtParentRoute && tabs.length > 0) {
      navigate(tabs[0].href, { replace: true });
    }
  }, [location.pathname, navigate, tabs]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    if (tabs[newValue]) {
      navigate(tabs[newValue].href);
    }
  };

  const handleDropdownChange = (event: SelectChangeEvent<string>) => {
    const selectedHref = event.target.value;
    if (selectedHref) {
      navigate(selectedHref);
    }
  };

  // Get the current tab href for the dropdown value
  const currentTabHref = tabs[tabValue]?.href || '';

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {isMobile ? (
        // Mobile: Show dropdown
        <FormControl fullWidth size="small" sx={{ mb: 2, flexShrink: 0 }} variant="outlined">
          <InputLabel id="tab-select-label">Page</InputLabel>
          <Select
            labelId="tab-select-label"
            id="tab-select"
            value={currentTabHref}
            label="Navigate to:"
            onChange={handleDropdownChange}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(0, 0, 0, 0.02)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
                '&.Mui-focused': {
                  backgroundColor: 'rgba(0, 0, 0, 0.03)',
                },
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 300,
                },
              },
            }}
          >
            {tabs.map((tab) => (
              <MenuItem
                key={tab.href}
                value={tab.href}
                sx={{
                  py: 1.5,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(25, 118, 210, 0.12)',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.18)',
                    },
                  },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {tab.label}
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : (
        // Desktop: Show tabs (existing implementation)
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{ flexShrink: 0 }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            {tabs.map((tab) => (
              <Tab key={tab.href} label={tab.label} />
            ))}
          </Tabs>
        </Box>
      )}
      <div style={{ flex: 1, minHeight: 0 }}>
        <Outlet />
      </div>
    </div>
  );
};

export default TabsWithOutlet;
